/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = Array.from(
        this.querySelectorAll('.option-selector[data-option-position]')
      ).sort((selectorA, selectorB) => {
        return Number(selectorA.dataset.optionPosition) - Number(selectorB.dataset.optionPosition);
      });
      this.packRelatedSelector = this.querySelector('[data-pack-related-selector]');
      this.packRelatedLoading = this.querySelector('[data-pack-related-loading]');
      this.packRelatedLinks = this.querySelectorAll('.js-pack-related-color-link');
      this.isPackRelatedLoading = false;
      this.sortPackRelatedOptionsStable();
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.bindPackRelatedSelectorEvents();
      this.handleOpenStepFromUrl();

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    connectedCallback() {
      this.handleOpenStepFromUrl();
    }

    handleOpenStepFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const openStep = urlParams.get('open_step');
      
      if (openStep) {
        const index = parseInt(openStep, 10) - 1;
        const tryOpen = () => {
          const disclosures = this.querySelectorAll('.pack-option-disclosure');
          const disclosure = disclosures[index];
          if (disclosure) {
            disclosure.open = true;
            disclosure.classList.add('is-open');
            disclosure.classList.remove('is-closing');
            
            const panel = disclosure.querySelector('.disclosure__panel');
            if (panel) {
              panel.style.height = 'auto';
              // Re-adjust after half a second to allow layout to settle
              setTimeout(() => {
                panel.style.height = `${panel.scrollHeight}px`;
              }, 500);
            }
            return true;
          }
          return false;
        };

        // Try immediately and a bit later
        if (!tryOpen()) {
          setTimeout(tryOpen, 200);
        }
        setTimeout(tryOpen, 1000); // Last resort
      }
    }

    bindPackRelatedSelectorEvents() {
      if (!this.section || this.packRelatedLinks.length === 0) return;

      this.packRelatedLinks.forEach((link) => {
        link.addEventListener('click', this.handlePackRelatedColorClick.bind(this));
      });
    }

    sortPackRelatedOptionsStable() {
      if (!this.packRelatedSelector) return;

      const list = this.packRelatedSelector.querySelector('.pdp-pack-related-color-selector');
      if (!list) return;

      const items = Array.from(list.querySelectorAll('.js-pack-related-color-link'));
      if (items.length < 2) return;

      items.sort((itemA, itemB) => {
        const labelA = itemA.dataset.relatedColorLabel?.trim() || '';
        const labelB = itemB.dataset.relatedColorLabel?.trim() || '';
        if (labelA && labelB && labelA !== labelB) {
          return labelA.localeCompare(labelB, 'es', { sensitivity: 'base' });
        }

        const idA = Number.parseInt(itemA.dataset.relatedProductId || '', 10);
        const idB = Number.parseInt(itemB.dataset.relatedProductId || '', 10);
        const hasNumericA = Number.isFinite(idA);
        const hasNumericB = Number.isFinite(idB);

        if (hasNumericA && hasNumericB && idA !== idB) {
          return idA - idB;
        }

        const fallbackIdA = itemA.dataset.relatedProductId || '';
        const fallbackIdB = itemB.dataset.relatedProductId || '';
        return fallbackIdA.localeCompare(fallbackIdB);
      });

      items.forEach((item) => {
        list.appendChild(item);
      });
    }

    setPackRelatedLoadingState(isLoading) {
      if (!this.packRelatedSelector) return;

      this.packRelatedSelector.classList.toggle('is-loading', isLoading);
      this.packRelatedSelector.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    collectSelectedOptionsByPosition() {
      const selectedOptionsByPosition = {};

      this.optionSelectors.forEach((selector) => {
        const position = selector.dataset.optionPosition;
        if (typeof position === 'undefined') return;

        let selectedValue = null;

        if (selector.dataset.selectorType === 'dropdown') {
          const selectedOption = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedValue = selectedOption ? selectedOption.dataset.value : null;

          if (!selectedValue) {
            const selectedText = selector.querySelector('.custom-select__btn')?.textContent.trim();
            if (selectedText && selectedText !== this.dataset.placeholderText) {
              selectedValue = selectedText;
            }
          }
        } else {
          selectedValue = selector.querySelector('input:checked')?.value || null;
        }

        if (selectedValue) {
          selectedOptionsByPosition[position] = selectedValue;
        }
      });

      return selectedOptionsByPosition;
    }

    async handlePackRelatedColorClick(evt) {
      evt.preventDefault();
      if (this.isPackRelatedLoading) return;

      const link = evt.currentTarget;
      const nextUrl = link?.dataset.relatedProductUrl || link?.getAttribute('href');
      if (!nextUrl) return;
      if (link.classList.contains('is-active')) return;

      const sectionContainer = this.section?.closest('.shopify-section');
      const sectionId = this.section?.dataset.section;
      if (!sectionContainer || !sectionId) {
        window.location.assign(nextUrl);
        return;
      }

      const selectedOptionsByPosition = this.collectSelectedOptionsByPosition();
      this.isPackRelatedLoading = true;
      this.setPackRelatedLoadingState(true);

      try {
        const separator = nextUrl.includes('?') ? '&' : '?';
        const sectionUrl = `${nextUrl}${separator}section_id=${encodeURIComponent(sectionId)}`;
        const response = await fetch(sectionUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Section render failed with status ${response.status}`);
        }

        const responseText = await response.text();
        const sectionMarkup = VariantPicker.extractSectionMarkup(responseText, sectionContainer.id);
        if (!sectionMarkup) {
          throw new Error('Section render returned empty markup');
        }

        window.history.pushState({}, '', nextUrl);
        sectionContainer.innerHTML = sectionMarkup;

        const refreshedSectionContainer = document.getElementById(sectionContainer.id);
        if (refreshedSectionContainer) {
          VariantPicker.reapplySavedSelectionsWithRetry(refreshedSectionContainer, selectedOptionsByPosition);
        }
      } catch (error) {
        console.error('Pack color selector fetch failed', error); // eslint-disable-line
        window.location.assign(nextUrl);
      } finally {
        this.isPackRelatedLoading = false;
        this.setPackRelatedLoadingState(false);
      }
    }

    static extractSectionMarkup(responseText, sectionContainerId) {
      const trimmedMarkup = responseText ? responseText.trim() : '';
      if (!trimmedMarkup) return '';

      const template = document.createElement('template');
      template.innerHTML = trimmedMarkup;

      const byId = sectionContainerId
        ? template.content.querySelector(`#${sectionContainerId}`)
        : null;
      const sectionEl = byId || template.content.querySelector('.shopify-section');

      if (sectionEl) {
        return sectionEl.innerHTML;
      }

      return trimmedMarkup;
    }

    static reapplySavedSelectionsWithRetry(sectionContainer, selectedOptionsByPosition, attempt = 0) {
      if (!selectedOptionsByPosition || Object.keys(selectedOptionsByPosition).length === 0) return;

      const picker = sectionContainer.querySelector('variant-picker');
      if (!picker) return;

      const applied = VariantPicker.applySavedSelections(picker, selectedOptionsByPosition);
      if (!applied && attempt < 5) {
        setTimeout(() => {
          VariantPicker.reapplySavedSelectionsWithRetry(sectionContainer, selectedOptionsByPosition, attempt + 1);
        }, 120);
      }
    }

    static applySavedSelections(picker, selectedOptionsByPosition) {
      const selectors = Array.from(
        picker.querySelectorAll('.option-selector[data-option-position]')
      ).sort((selectorA, selectorB) => {
        return Number(selectorA.dataset.optionPosition) - Number(selectorB.dataset.optionPosition);
      });

      let appliedAny = false;

      selectors.forEach((selector) => {
        const optionPosition = selector.dataset.optionPosition;
        const savedValue = selectedOptionsByPosition[optionPosition];
        if (!savedValue) return;

        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          if (!customSelect) return;

          const optionToSelect = Array.from(customSelect.querySelectorAll('.custom-select__option')).find((optionEl) => {
            return optionEl.dataset.value === savedValue
              && !optionEl.classList.contains('is-unavailable')
              && !optionEl.classList.contains('is-nonexistent')
              && !optionEl.classList.contains('is-disabled');
          });

          if (optionToSelect) {
            customSelect.selectOption(optionToSelect);
            appliedAny = true;
          }
        } else {
          const inputToSelect = Array.from(selector.querySelectorAll('.js-option')).find((optionEl) => {
            return optionEl.value === savedValue
              && !optionEl.disabled
              && !optionEl.classList.contains('is-unavailable')
              && !optionEl.classList.contains('is-nonexistent');
          });

          if (inputToSelect) {
            if (inputToSelect.checked) {
              inputToSelect.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              inputToSelect.click();
            }
            appliedAny = true;
          }
        }
      });

      return appliedAny;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      // this.showOrHiddenMedia(evt, this)
      this.scrollToSlider(evt, this)
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    triggerOpenPopupOutStock() {
      if(document.querySelector('.restock-rocket-button-cover')) {
        document.querySelector('.restock-rocket-button-cover').click();
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      // this.addBtn.disabled = !variantAvailable || this.preSelection;
      if(!variantAvailable || this.preSelection) {
        this.addBtn.classList.add('disabled');
        this.addBtn.type = 'button';
        this.addBtn.addEventListener('click', this.triggerOpenPopupOutStock)
      } else {
        this.addBtn.classList.remove('disabled');
        this.addBtn.type = 'submit';
        this.addBtn.removeEventListener('click', this.triggerOpenPopupOutStock)
      }

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.innerHTML = variantAvailable
            ? this.addBtn.dataset.addToCartText
            : unavailableStr;
        this.addBtn.disabled = false;
        this.addBtn.classList.remove('btn-not-selected');
      }
    }

    scrollToSlider(evt, self) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      let labelText = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      let mediaGallery = document.querySelector('media-gallery');
      if(mediaGallery && labelText) {
        let variantMedia = mediaGallery.querySelector('.media-viewer').querySelector(`li[data-media-color="${labelText}"]`);
        mediaGallery.customSetActiveMedia(variantMedia, true);
      }
    }
    showOrHiddenMedia(evt, self) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;


      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      let labelText = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      let mediaGallery = document.querySelector('media-gallery');
      if(labelText && mediaGallery) {
        mediaGallery.querySelectorAll('.media-viewer__item').forEach(item => {
          item.classList.toggle('md:hidden', item.getAttribute('data-media-color') !== labelText)
        })
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if(this.closest('.quick_add-product-card') && window.quickAdd == true) {
        return;
      }

      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        // this.querySelectorAll('.js-option').forEach((optionEl) => {
        //   VariantPicker.updateOptionAvailability(optionEl, false, false);
        // });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
            `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const productInfoPercent = this.price.querySelector('.product-info__percent');
        // Update current price and original price if on sale.

        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl) {
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';
        }
        let price = parseFloat(this.data.formatted[this.variant.id].price.replace(/[^\d.]/g, ''));
        let compareAtPrice = parseFloat((this.data.formatted[this.variant.id].compareAtPrice || '0').replace(/[^\d.]/g, ''));
        if(productInfoPercent) {
          if (compareAtPrice <= price) {
            productInfoPercent.innerHTML = '';
            productInfoPercent.style.display = 'none';
          } else {
            let percent = Math.ceil((100 - (price / compareAtPrice * 100)));
            productInfoPercent.innerHTML = `-${percent}%`;
            productInfoPercent.style.display = 'block';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
